import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../styles/HomeownerLanding.css';

import proinsightLogo from '../assets/images/proinsght-logo--1024x256.png';
import goldWhiteLogo from '../assets/images/Gold-and-White-Logo-1024x337.png';
import heroImg from '../assets/images/Homeowner-Concierge-Marketing-1-video-poster.png';
import problemImg from '../assets/images/Untitled-design-modified-1024x1024.png';
import catPlumbing from '../assets/images/cat-plumbing.jpg';
import catLandscaping from '../assets/images/cat-landscaping.jpg';
import catElectrical from '../assets/images/cat-electrical.jpg';
import catRealEstate from '../assets/images/cat-realestate.jpg';
import catEstate from '../assets/images/cat-estate.jpg';
import catFinance from '../assets/images/cat-finance.jpg';
import homeKitchen from '../assets/images/home-kitchen.jpg';
import mapGraphic from '../assets/images/Map-Graphic-1024x683.png';
import stepsMediaImg from '../assets/images/Homeowner-Concierge-1-1024x585.png';
import homeExterior from '../assets/images/home-exterior.jpg';
import oneProBadge from '../assets/images/one-pro-badge.png';

export default function HomeownerLanding() {
    const [navOpen, setNavOpen] = useState(false);
    const [showAccessModal, setShowAccessModal] = useState(false);
    const [heroVideoStarted, setHeroVideoStarted] = useState(false);
    const heroVideoRef = useRef(null);

    function playHeroVideo() {
        setHeroVideoStarted(true);
        heroVideoRef.current.play();
    }

    useEffect(() => {
        if (!('IntersectionObserver' in window)) {
            document.querySelectorAll('.hoc-landing .reveal').forEach(el => el.classList.add('in'));
            return;
        }
        const io = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('in');
                    io.unobserve(entry.target);
                }
            });
        }, { threshold: 0.12 });
        document.querySelectorAll('.hoc-landing .reveal').forEach(el => io.observe(el));
        return () => io.disconnect();
    }, []);

    useEffect(() => {
        if (!showAccessModal) return;
        const scriptSrc = "https://link.proinsight.com/js/form_embed.js";
        if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
            const script = document.createElement('script');
            script.src = scriptSrc;
            document.body.appendChild(script);
        }
    }, [showAccessModal]);

    function openAccessModal() {
        setNavOpen(false);
        setShowAccessModal(true);
    }

    return (
        <div className="hoc-landing">
            <header className="site-header">
                <div className="container">
                    <a className="logo" href="https://proinsight.com/"><img src={proinsightLogo} alt="ProInsight Homeowner Concierge™" /></a>
                    <button className="nav-toggle" aria-label="Open menu" aria-expanded={navOpen} aria-controls="nav" onClick={() => setNavOpen(!navOpen)}>
                        <span></span><span></span><span></span>
                    </button>
                    <nav className={`nav ${navOpen ? 'open' : ''}`} id="nav">
                        <a href="#best" onClick={() => setNavOpen(false)}>The Community</a>
                        <a href="#why" onClick={() => setNavOpen(false)}>Why the Concierge</a>
                        <a href="#how" onClick={() => setNavOpen(false)}>How It Works</a>
                        <button className="btn" onClick={openAccessModal}>Get Concierge Access</button>
                    </nav>
                </div>
            </header>

            <main>
                <section className="hero">
                    <div className="container">
                        <div className="reveal">
                            <span className="eyebrow">The Homeowner Concierge™</span>
                            <h1>Your home and estate, <strong>handled.</strong></h1>
                            <p className="lead">Meet your ProInsight Homeowner Concierge: <b>one trusted community of the very best local professionals</b> for everything your home and estate need.</p>
                            <button className="btn" onClick={openAccessModal}>Get Concierge Access</button>
                            <div className="trust-line">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DAB559" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 2l7 3v6c0 5-3.5 9-7 11-3.5-2-7-6-7-11V5l7-3z" /><path d="M9 12l2 2 4-4" /></svg>
                                By invitation from a professional you already trust.
                            </div>
                        </div>
                        <div className="hero-media reveal">
                            <video
                                ref={heroVideoRef}
                                src="https://assets.cdn.filesafe.space/sIF5ZS1VxhUZsYbvq7gL/media/6ae4d26009f888e023f54eb2.mp4"
                                poster={heroImg}
                                controls={heroVideoStarted}
                                playsInline
                                preload="metadata"
                                aria-label="Homeowner Concierge overview video"
                            />
                            {!heroVideoStarted && (
                                <button type="button" className="hero-play" onClick={playHeroVideo} aria-label="Play video">
                                    <span className="hero-play-icon">
                                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4.5v15l12.5-7.5z" /></svg>
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>
                </section>

                <section className="problem">
                    <div className="container">
                        <div className="reveal">
                            <h2>Finding good help shouldn't be a gamble.</h2>
                            <ul className="pain-list">
                                <li>Endless search results.</li>
                                <li>Reviews you can't trust.</li>
                                <li>Callbacks that never come.</li>
                            </ul>
                            <p>Most homeowners spend more time finding a pro than the job itself takes.</p>
                            <p className="kicker">There's a better way.</p>
                        </div>
                        <img className="reveal" src={problemImg} alt="A frustrated homeowner rubbing their eyes at a desk" />
                    </div>
                </section>

                <section className="best" id="best">
                    <div className="container">
                        <div className="reveal">
                            <span className="eyebrow">Your Local Community</span>
                            <h2>100 of the best, all in one place.</h2>
                            <p className="intro">We identified 100 professionals across 100 categories, each recommended by the internet, by another trusted professional, or both, as the very best in your local community. From plumbing and landscaping to estate planning and financial services, the right expert is already here.</p>
                        </div>
                        <div className="stats reveal">
                            <div className="stat"><div className="num">100</div><div className="label">Top local pros</div></div>
                            <div className="stat"><div className="num">100</div><div className="label">Categories</div></div>
                            <div className="stat"><div className="num"><span>1</span></div><div className="label">Pro per category</div></div>
                        </div>
                        <div className="cat-grid reveal">
                            <div className="cat"><img src={catPlumbing} alt="" loading="lazy" /><span>Plumbing</span></div>
                            <div className="cat"><img src={catLandscaping} alt="" loading="lazy" /><span>Landscaping</span></div>
                            <div className="cat"><img src={catElectrical} alt="" loading="lazy" /><span>Electrical</span></div>
                            <div className="cat"><img src={catRealEstate} alt="" loading="lazy" /><span>Real Estate</span></div>
                            <div className="cat"><img src={catEstate} alt="" loading="lazy" /><span>Estate Planning</span></div>
                            <div className="cat"><img src={catFinance} alt="" loading="lazy" /><span>Financial Services</span></div>
                            <div className="cat"><img src={homeKitchen} alt="" loading="lazy" /><span>Remodeling</span></div>
                            <div className="cat cat-more"><div className="big">+93</div><p>more categories covering everything your home and estate need.</p></div>
                        </div>
                    </div>
                </section>

                <section className="why" id="why">
                    <div className="container">
                        <div className="why-head reveal">
                            <span className="eyebrow">A Higher Standard</span>
                            <h2>Why homeowners choose the Concierge</h2>
                        </div>
                        <div className="why-grid">
                            <article className="card reveal">
                                <img className="icon icon-badge" src={oneProBadge} alt="" />
                                <h3>One pro per category, by design.</h3>
                                <p>Each professional is exclusively selected in their category. That spot is earned, and they work hard to keep it.</p>
                            </article>
                            <article className="card reveal">
                                <svg className="icon" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="32" cy="18" r="8" /><circle cx="12" cy="28" r="6" /><circle cx="52" cy="28" r="6" /><path d="M18 54c0-8 6-14 14-14s14 6 14 14" /><path d="M2 52c0-6 4-10 10-10 2.5 0 4.5.6 6 1.8" /><path d="M62 52c0-6-4-10-10-10-2.5 0-4.5.6-6 1.8" /></svg>
                                <h3>A community that holds itself to a higher standard.</h3>
                                <p>Every pro wants to protect their good standing with the other top professionals in the community. Their reputation is on the line with their peers, not just with you.</p>
                            </article>
                            <article className="card reveal">
                                <svg className="icon" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M32 5l21 8v15c0 14-9.5 25-21 31C20.5 53 11 42 11 28V13z" /><path d="M22 31l7 7 13-14" /></svg>
                                <h3>Accountability built in.</h3>
                                <p>You were invited by a professional you already trust. When you choose a service on the platform, that professional is notified, so the person who vouched for the community stays in the loop.</p>
                            </article>
                            <article className="card reveal">
                                <svg className="icon" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="32" cy="34" r="24" /><path d="M32 20v14l9 6" /><path d="M26 4h12" /><path d="M32 4v6" /></svg>
                                <h3>Your time back.</h3>
                                <p>No more searching, vetting, or guessing. The best local pros are ready when you need them.</p>
                            </article>
                        </div>
                        <div className="why-feature reveal">
                            <img src={mapGraphic} alt="ProInsight Concierge pin over a map of the United States" style={{ objectFit: 'contain', background: '#fff', padding: '20px' }} />
                            <div className="copy">
                                <span className="eyebrow">Exclusive by Design</span>
                                <h3>Every spot in your community is earned.</h3>
                                <p>Only one professional holds each category in your local community, and they know their standing depends on how well they take care of you.</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="steps" id="how">
                    <div className="container">
                        <div className="reveal">
                            <span className="eyebrow">How It Works</span>
                            <h2>Simple as 1, 2, 3</h2>
                            <div className="step">
                                <div className="n">1</div>
                                <div><h3>Accept your invitation.</h3><p>You're here because a trusted professional invited you.</p></div>
                            </div>
                            <div className="step">
                                <div className="n">2</div>
                                <div><h3>Choose a service.</h3><p>Browse 100 categories covering your home and estate.</p></div>
                            </div>
                            <div className="step">
                                <div className="n">3</div>
                                <div><h3>Connect with the best.</h3><p>Work with a pro who is committed to keeping their place in the community.</p></div>
                            </div>
                        </div>
                        <div className="steps-media reveal">
                            <img src={stepsMediaImg} alt="Homeowner Concierge app screens showing verified local professional profiles" />
                        </div>
                    </div>
                </section>

                <section className="cta" id="access" style={{ backgroundImage: `url(${homeExterior})` }}>
                    <div className="container reveal">
                        <h2>Stop searching. <span>Start trusting.</span></h2>
                        <p>Your home and estate deserve the very best in your community, and now you have them in one place.</p>
                        <button className="btn btn-gold" onClick={openAccessModal}>Get Concierge Access</button>
                    </div>
                </section>
            </main>

            <footer className="site-footer">
                <div className="container">
                    <div className="footer-top">
                        <a href="https://proinsight.com/"><img src={goldWhiteLogo} alt="ProInsight™" /></a>
                        <nav className="footer-links">
                            <a href="https://proinsight.com/">Home</a>
                            <a href="https://proinsight.com/about-us/">About Us</a>
                            <a href="https://proinsight.com/blog/">Blog</a>
                        </nav>
                    </div>
                    <div className="footer-bottom">
                        <div>
                            <a href="https://proinsight.com/privacy-policy/">Privacy Policy</a> |{' '}
                            <a href="https://proinsight.com/terms-conditions/">Terms &amp; Conditions</a> |{' '}
                            ©2026 ProInsight™
                        </div>
                        <div>403 Portway Avenue, Ste 300, Hood River, OR 97031</div>
                    </div>
                </div>
            </footer>

            {showAccessModal && createPortal(
                <div className="hoc-modal-overlay" onClick={() => setShowAccessModal(false)}>
                    <div className="hoc-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="hoc-modal-close" onClick={() => setShowAccessModal(false)} aria-label="Close">×</button>
                        <h3 className="hoc-modal-title">Allow us to connect you with a community nearest you.</h3>
                        <div className="hoc-modal-iframe-wrap">
                            <iframe
                                src="https://link.proinsight.com/widget/form/tNcP3z5vs8dKcA9fkCen"
                                style={{ width: '100%', height: '100%', border: 'none', borderRadius: '8px' }}
                                id="inline-tNcP3z5vs8dKcA9fkCen"
                                data-layout="{'id':'INLINE'}"
                                data-trigger-type="alwaysShow"
                                data-trigger-value=""
                                data-activation-type="alwaysActivated"
                                data-activation-value=""
                                data-deactivation-type="neverDeactivate"
                                data-deactivation-value=""
                                data-form-name="Homeowner Intake Form"
                                data-height="434"
                                data-layout-iframe-id="inline-tNcP3z5vs8dKcA9fkCen"
                                data-form-id="tNcP3z5vs8dKcA9fkCen"
                                data-cookie-consent="true"
                                data-cookie-consent-provider="auto"
                                title="Homeowner Intake Form"
                            />
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
